import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem, Typography, CircularProgress, Card, CardContent } from '@mui/material';
import { FetchOrganizations, CreateEntity } from '../../../../services/api';
import CustomAlert from '../../../../components/CustomAlert/CustomAlert';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const CreateEntityForm = ({ onClose, onSuccess }) => {
    // Master data
    const [organizations, setOrganizations] = useState([]);
    const [startLoading, setStartLoading] = useState(false);

    // Alert state
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('success');

    const token = localStorage.getItem('accessToken');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setStartLoading(true);
        try {
            const orgResp = await FetchOrganizations(token);
            if (orgResp?.data?.data) {
                setOrganizations(orgResp.data.data);
            }
        } catch (error) {
            console.error("Error loading data", error);
        }
        setStartLoading(false);
    };

    const validationSchema = Yup.object({
        name: Yup.string().trim().min(2, 'Name must be at least 2 characters').required('Entity Name is required'),
        code: Yup.string().trim().required('Entity Code is required'),
        type: Yup.string().required('Type is required'),
        organizationId: Yup.string().required('Organization is required'),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            code: '',
            type: 'Plant',
            organizationId: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);
            const payload = {
                entityName: values.name,
                entityCode: values.code,
                entityType: values.type,
                organizationId: values.organizationId,
                isActive: true,
                metadata: {}
            };

            try {
                const resp = await CreateEntity(payload, token);
                if (resp?.data) {
                    setAlertMessage('Entity created successfully');
                    setAlertSeverity('success');
                    setAlertOpen(true);
                    setTimeout(() => {
                        if (onSuccess) onSuccess();
                        if (onClose) onClose();
                    }, 1500);
                } else {
                    setAlertMessage(resp?.response?.data?.message || 'Failed to create entity');
                    setAlertSeverity('error');
                    setAlertOpen(true);
                }
            } catch (error) {
                setAlertMessage('An unexpected error occurred');
                setAlertSeverity('error');
                setAlertOpen(true);
            } finally {
                setSubmitting(false);
            }
        },
    });

    if (startLoading) return <CircularProgress />;

    return (
        <Card>
            <CustomAlert open={alertOpen} handleClose={() => setAlertOpen(false)} message={alertMessage} severity={alertSeverity} />
            <CardContent>
                <Typography variant="h6" gutterBottom>Create New Entity</Typography>
                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                id="name"
                                name="name"
                                label="Entity Name *"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                id="code"
                                name="code"
                                label="Entity Code *"
                                value={formik.values.code}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.code && Boolean(formik.errors.code)}
                                helperText={formik.touched.code && formik.errors.code}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    id="type"
                                    name="type"
                                    value={formik.values.type}
                                    label="Type"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                >
                                    <MenuItem value="Plant">Plant</MenuItem>
                                    <MenuItem value="Office">Office</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth error={formik.touched.organizationId && Boolean(formik.errors.organizationId)}>
                                <InputLabel>Organization *</InputLabel>
                                <Select
                                    id="organizationId"
                                    name="organizationId"
                                    value={formik.values.organizationId}
                                    label="Organization *"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                >
                                    {organizations.map(org => (
                                        <MenuItem key={org.id} value={org.id}>{org.organizationName}</MenuItem>
                                    ))}
                                </Select>
                                {formik.touched.organizationId && formik.errors.organizationId && (
                                    <Typography color="error" variant="caption" sx={{ ml: 2, mt: 0.5 }}>
                                        {formik.errors.organizationId}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                        <Button onClick={onClose} variant="outlined">Cancel</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={formik.isSubmitting || !formik.isValid}
                        >
                            Create Entity
                        </Button>
                    </Box>
                </form>
            </CardContent>
        </Card>
    );
};

export default CreateEntityForm;
